/**
 * TwoPoint Widget - Main Entry Point
 * CDN-ready iframe integration for TwoPoint navigators.
 *
 * Event bus:
 * - URL ?twopoint-event=<name>&twopoint-<param>=... (legacy ?property=ID kept).
 * - DOM autowiring: <span twopoint-event="..." twopoint-id="...">.
 * - JS API: TwoPointWidget.dispatch(name, params) / instance.dispatch(...).
 */

import type { TwoPointWidgetConfig, WidgetInstance, EventParams } from './types';
import { generateUniqueId, getDefaultStyles, injectStyles, debugLog } from './utils';
import { sendHandshake } from './handshake';
import { createMessageHandler } from './message-handler';
import { dispatchEvent as dispatchToWidgets } from './dispatcher';
import { expandEvent } from './event-registry';
import { DEEP_LINK_PARAM, EVENT_PARAM, PARAM_PREFIX } from './config';

// Version injected by build process
declare const __VERSION__: string;
declare const __BUILD_DATE__: string;

const DEFAULT_CONFIG: Required<TwoPointWidgetConfig> = {
  iframeSelector: '.twopoint-iframe',
  maxRetries: 5,
  retryDelay: 100,
  debugMode: false,
  injectStyles: true,
  onHeightUpdate: () => {},
  onScrollRequest: () => {},
  onHandshakeComplete: () => {},
  onHandshakeFailed: () => {}
};

class TwoPointWidget {
  private static instance: TwoPointWidget | null = null;
  private config: Required<TwoPointWidgetConfig>;
  private widgets: Map<string, WidgetInstance> = new Map();
  private messageHandlerBound: ((event: MessageEvent) => void) | null = null;
  private clickHandlerBound: ((event: MouseEvent) => void) | null = null;
  private initialized: boolean = false;

  private constructor(config: TwoPointWidgetConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  static init(config: TwoPointWidgetConfig = {}): TwoPointWidget {
    if (!TwoPointWidget.instance) {
      TwoPointWidget.instance = new TwoPointWidget(config);
      TwoPointWidget.instance.setup();
    } else {
      console.warn('[TwoPoint Widget] Already initialized. Use getInstance() to access the instance.');
    }
    return TwoPointWidget.instance;
  }

  static getInstance(): TwoPointWidget | null {
    return TwoPointWidget.instance;
  }

  static get version(): string {
    return typeof __VERSION__ !== 'undefined' ? __VERSION__ : '1.0.0';
  }

  /**
   * Static shortcut — dispatches via the singleton instance.
   * Throws if init() has not run.
   */
  static dispatch(name: string, params: EventParams = {}): void {
    if (!TwoPointWidget.instance) {
      throw new Error('[TwoPoint Widget] dispatch called before init()');
    }
    TwoPointWidget.instance.dispatch(name, params);
  }

  private setup(): void {
    if (this.initialized) {
      debugLog(this.config.debugMode, 'Widget already initialized');
      return;
    }

    debugLog(this.config.debugMode, `Initializing TwoPoint Widget v${TwoPointWidget.version}`);

    if (this.config.injectStyles) {
      injectStyles(getDefaultStyles());
    }

    this.initializeIframes();

    this.messageHandlerBound = createMessageHandler(this.config);
    window.addEventListener('message', this.messageHandlerBound);

    this.clickHandlerBound = (event: MouseEvent) => this.handleAutowiredClick(event);
    document.addEventListener('click', this.clickHandlerBound);

    this.dispatchUrlEventParentSide();

    this.initialized = true;
    debugLog(this.config.debugMode, `Initialized ${this.widgets.size} widget(s)`);
  }

  /**
   * Find iframes, propagate twopoint-* (and legacy property) params to iframe.src,
   * register instances, and trigger handshake.
   */
  private initializeIframes(): void {
    const iframes = document.querySelectorAll<HTMLIFrameElement>(this.config.iframeSelector);
    const propagated = collectPropagatedParams(window.location.search);

    iframes.forEach((iframe) => {
      if (propagated.size > 0 && iframe.src) {
        const iframeUrl = new URL(iframe.src);
        propagated.forEach((value, key) => {
          iframeUrl.searchParams.set(key, value);
        });
        iframe.src = iframeUrl.toString();
      }

      if (!iframe.id) {
        iframe.id = generateUniqueId();
      }

      const widgetId = iframe.id;

      if (this.widgets.has(widgetId)) {
        debugLog(this.config.debugMode, `Widget ${widgetId} already initialized`);
        return;
      }

      const instance: WidgetInstance = {
        widgetId,
        iframe,
        currentHeight: null,
        handshakeComplete: false
      };

      this.widgets.set(widgetId, instance);

      iframe.addEventListener('load', () => {
        debugLog(this.config.debugMode, 'Load event for', widgetId);
        sendHandshake(iframe, this.config, 0);
      });

      setTimeout(() => {
        debugLog(this.config.debugMode, 'Delayed handshake attempt for', widgetId);
        sendHandshake(iframe, this.config, 0);
      }, 500);
    });
  }

  /**
   * Read URL on host page, expand the event, run parent-kind commands locally.
   * Iframe-kind commands are handled by the iframe app (it reads the same URL).
   */
  private dispatchUrlEventParentSide(): void {
    const urlEvent = readUrlEvent(window.location.search);
    if (!urlEvent) return;

    let commands;
    try {
      commands = expandEvent(urlEvent.name, urlEvent.params);
    } catch (e) {
      console.warn('[TwoPoint Widget] URL event rejected:', (e as Error).message);
      return;
    }

    const parentCommands = commands.filter((c) => c.kind === 'parent');
    if (parentCommands.length === 0) return;

    // Defer to next tick so iframes are in DOM and visible.
    setTimeout(() => {
      this.widgets.forEach((widget) => {
        parentCommands.forEach((cmd) => {
          if (cmd.kind === 'parent' && cmd.action === 'widget.scrollIntoView') {
            widget.iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });
    }, 0);
  }

  private handleAutowiredClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target || typeof target.closest !== 'function') return;

    const trigger = target.closest('[twopoint-event]') as HTMLElement | null;
    if (!trigger) return;

    const name = trigger.getAttribute(EVENT_PARAM);
    if (!name) return;

    const params: EventParams = {};
    for (let i = 0; i < trigger.attributes.length; i++) {
      const attr = trigger.attributes[i];
      if (attr.name === EVENT_PARAM) continue;
      if (!attr.name.startsWith(PARAM_PREFIX)) continue;
      params[attr.name.slice(PARAM_PREFIX.length)] = attr.value;
    }

    try {
      this.dispatch(name, params);
    } catch (err) {
      console.error('[TwoPoint Widget]', (err as Error).message);
    }
  }

  /**
   * Public runtime API. Expand the named event via registry; execute
   * parent-kind commands locally, post iframe-kind commands to each widget.
   * Throws on unknown event name or invalid params.
   */
  public dispatch(name: string, params: EventParams = {}): void {
    dispatchToWidgets(name, params, this.widgets);
  }

  public addIframe(iframe: HTMLIFrameElement): void {
    if (!iframe.id) {
      iframe.id = generateUniqueId();
    }

    const widgetId = iframe.id;

    if (this.widgets.has(widgetId)) {
      debugLog(this.config.debugMode, `Widget ${widgetId} already exists`);
      return;
    }

    const instance: WidgetInstance = {
      widgetId,
      iframe,
      currentHeight: null,
      handshakeComplete: false
    };

    this.widgets.set(widgetId, instance);

    if (iframe.contentWindow) {
      sendHandshake(iframe, this.config, 0);
    }
  }

  public removeWidget(widgetId: string): void {
    this.widgets.delete(widgetId);
    debugLog(this.config.debugMode, `Widget ${widgetId} removed`);
  }

  public getWidgets(): Map<string, WidgetInstance> {
    return new Map(this.widgets);
  }

  public destroy(): void {
    if (this.messageHandlerBound) {
      window.removeEventListener('message', this.messageHandlerBound);
      this.messageHandlerBound = null;
    }
    if (this.clickHandlerBound) {
      document.removeEventListener('click', this.clickHandlerBound);
      this.clickHandlerBound = null;
    }

    this.widgets.clear();
    this.initialized = false;
    TwoPointWidget.instance = null;

    debugLog(this.config.debugMode, 'Widget destroyed');
  }
}

interface UrlEvent {
  name: string;
  params: EventParams;
}

const readUrlEvent = (search: string): UrlEvent | null => {
  const params = new URLSearchParams(search);
  const eventName = params.get(EVENT_PARAM);

  if (eventName) {
    const eventParams: EventParams = {};
    params.forEach((value, key) => {
      if (key === EVENT_PARAM) return;
      if (!key.startsWith(PARAM_PREFIX)) return;
      eventParams[key.slice(PARAM_PREFIX.length)] = value;
    });
    return { name: eventName, params: eventParams };
  }

  const legacyId = params.get(DEEP_LINK_PARAM);
  if (legacyId) {
    return { name: 'property.open', params: { id: legacyId } };
  }

  return null;
};

/**
 * Build the set of params propagated from host URL to iframe.src.
 * Includes everything with `twopoint-` prefix plus legacy `property`.
 */
const collectPropagatedParams = (search: string): URLSearchParams => {
  const out = new URLSearchParams();
  const src = new URLSearchParams(search);
  src.forEach((value, key) => {
    if (key === DEEP_LINK_PARAM || key.startsWith(PARAM_PREFIX)) {
      out.set(key, value);
    }
  });
  return out;
};

export default TwoPointWidget;

if (typeof window !== 'undefined') {
  (window as any).TwoPointWidget = TwoPointWidget;

  const autoInit = (): void => {
    const scriptTag = document.querySelector('script[src*="twopoint-widget"][data-auto-init]');
    if (!scriptTag) return;
    const debugMode = scriptTag.getAttribute('data-debug') === 'true';
    TwoPointWidget.init({ debugMode });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}
